/**
 * Presentational call surface — avatar, status, captions, mic notice.
 *
 * Extracted from `MeetingBot` so the SAME UI renders in two very different places
 * from two different state sources:
 *
 *  - `MeetingBot` drives it from its own Pipecat client (standalone app, and the
 *    headless Recall/Attendee camera feed).
 *  - `InAppMeetingPanel` drives it from state relayed over `@/lib/callChannel`,
 *    because embedded in Fabric the call itself must run in a popup that owns the
 *    microphone.
 *
 * Every component here is PURE: props in, markup out. No Pipecat context, no
 * hooks beyond i18n. That is what lets the panel render it with no peer connection
 * of its own — and it means the embedded and non-embedded calls can no longer
 * drift apart visually, which they had already started to do.
 *
 * The one exception is bot video: `<PipecatClientVideo>` needs the Pipecat
 * provider, so it is injected as `videoSlot` by whoever actually has one rather
 * than imported here.
 */
import React from 'react'
import { Ear, EarOff } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { micFailureI18nKey, type MicFailure } from '@/lib/micDiagnostics'
import type {
  CallConnectionStatus,
  CallConversationStates,
  CallPersona,
  CallUiState,
} from '@/lib/callChannel'

export const AnimationStyles: React.FC = () => (
  <style>{`
    @keyframes talking-halo {
      0% {
        opacity: 0.3;
        transform: scale(1);
      }
      100% {
        opacity: 0.6;
        transform: scale(1.1);
      }
    }
    
    @keyframes talking-ring {
      0% {
        opacity: 0.8;
        transform: scale(1);
      }
      50% {
        opacity: 0.4;
        transform: scale(1.05);
      }
      100% {
        opacity: 0.8;
        transform: scale(1);
      }
    }
  `}</style>
)

interface AgentAvatarProps {
  agentPersona: CallPersona | null
  isTalking: boolean
  /** Bot video element, supplied only by a caller that has a Pipecat provider. */
  videoSlot?: React.ReactNode
  size: number
}

export const AgentAvatar: React.FC<AgentAvatarProps> = ({
  agentPersona,
  isTalking,
  videoSlot,
  size,
}) => {
  const { t } = useTranslation()
  const haloSize = size + 20
  const ringSize = size + 10
  return (
    <div className="relative inline-block">
      {videoSlot ? (
        videoSlot
      ) : (
        <>
          {isTalking && (
            <div
              className="absolute inset-0 rounded-full animate-pulse"
              style={{
                background:
                  'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.2) 70%, transparent 100%)',
                width: `${haloSize}px`,
                height: `${haloSize}px`,
                left: '-10px',
                top: '-10px',
                animation: 'talking-halo 1.5s ease-in-out infinite alternate',
              }}
            />
          )}

          {isTalking && (
            <div
              className="absolute inset-0 rounded-full border-4 border-blue-400"
              style={{
                width: `${ringSize}px`,
                height: `${ringSize}px`,
                left: '-5px',
                top: '-5px',
                animation: 'talking-ring 2s ease-in-out infinite',
              }}
            />
          )}

          <img
            src={agentPersona?.image}
            className="rounded-full object-cover relative z-10"
            style={{ width: `${size}px`, height: `${size}px` }}
            alt={agentPersona?.name || t('digital_coworker:meetingBot.agentAlt', { defaultValue: 'AI Agent' })}
          />
        </>
      )}
    </div>
  )
}

// Status Display Component
interface StatusDisplayProps {
  connectionStatus: CallConnectionStatus
  conversationStates: CallConversationStates
  botEnabled: boolean
  transcript: { userText: string; botText: string }
  agentPersona: CallPersona | null
  compact?: boolean
}

export const StatusDisplay: React.FC<StatusDisplayProps> = ({
  connectionStatus,
  conversationStates,
  botEnabled,
  transcript,
  agentPersona,
  compact,
}) => {
  const { t } = useTranslation()
  const userTryingToSpeak = Boolean(transcript.userText && transcript.userText.trim().length > 0)
  const botIsIdle = !conversationStates.listening
  const textCls = compact ? 'text-white text-sm' : 'text-white text-2xl'
  const orangeCls = compact ? 'text-orange-400 text-sm' : 'text-orange-400 text-2xl'

  return (
    <div className={`text-center ${compact ? 'mt-2' : 'mt-6'} flex flex-col items-center justify-center`}>
      {/* Connection status - always visible, fixed position */}
      <div className={`${compact ? 'h-5' : 'h-7'} flex items-center justify-center`}>
        <p className={textCls}>
          {connectionStatus === 'connected'
            ? t('digital_coworker:meetingBot.connected', { defaultValue: 'Connected' })
            : connectionStatus === 'connecting'
              ? t('digital_coworker:meetingBot.connecting', { defaultValue: 'Connecting...' })
              : connectionStatus === 'error'
                ? t('digital_coworker:meetingBot.error', { defaultValue: 'Error' })
                : t('digital_coworker:meetingBot.disconnected', { defaultValue: 'Disconnected' })}
        </p>
      </div>

      {/* Conversation state - fixed height container to prevent shifts */}
      <div className={`${compact ? 'h-8 mt-2' : 'h-16 mt-6'} flex items-center justify-center`}>
        {connectionStatus === 'connected' && (
          <>
            {!botEnabled ? (
              <p className={orangeCls}>{t('digital_coworker:meetingBot.muted', { defaultValue: 'Muted' })}</p>
            ) : !conversationStates.botListening ? (
              // The listen gate is off (/listenoff, or the automatic switch during a lookup):
              // the bot cannot hear the call, so the tile must not claim it is listening.
              <p className={orangeCls}>{t('digital_coworker:meetingBot.listenOff', { defaultValue: 'Listening off' })}</p>
            ) : (
              <>
                {conversationStates.functionCalling && (
                  <div className="flex flex-col items-center">
                    <p className={textCls}>{t('digital_coworker:meetingBot.processingListening', { defaultValue: 'Processing & Listening...' })}</p>
                  </div>
                )}
                {!conversationStates.functionCalling && botIsIdle && userTryingToSpeak && (
                  <p className={orangeCls}>
                    {t('digital_coworker:meetingBot.sayName', { name: agentPersona?.name || t('digital_coworker:meetingBot.agentNameFallback', { defaultValue: 'agent name' }), defaultValue: 'Say {{name}}...' })}
                  </p>
                )}
                {!conversationStates.functionCalling && conversationStates.listening && !conversationStates.thinking && !conversationStates.talking && (
                  <p className={textCls}>{t('digital_coworker:meetingBot.listening', { defaultValue: 'Listening...' })}</p>
                )}
                {!conversationStates.functionCalling && conversationStates.thinking && (
                  <p className={textCls}>{t('digital_coworker:meetingBot.processingListening', { defaultValue: 'Processing & Listening...' })}</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Transcript Display Component
interface TranscriptDisplayProps {
  transcript: { userText: string; botText: string }
  enabled: boolean
  compact?: boolean
}


export const TranscriptDisplay: React.FC<TranscriptDisplayProps> = ({ transcript, enabled, compact }) => {
  return (
    <div className={`text-center w-full ${compact ? 'max-w-xs px-2' : 'max-w-2xl px-6 -mt-8'}`}>
      {/* Fixed height container to prevent layout shift */}
      <div
        className={
          compact
            ? 'min-h-[40px] max-h-[40px] overflow-hidden flex items-end justify-center'
            : 'min-h-[90px] max-h-[90px] overflow-hidden flex items-end justify-center'
        }
      >
        <p className={compact ? 'text-white text-sm' : 'text-white text-2xl'}>
          {transcript.botText || (enabled ? transcript.userText : '')}
        </p>
      </div>
    </div>
  )
}

interface MicNoticeProps {
  failure: MicFailure | null
  compact?: boolean
}

/**
 * Surfaces a mic acquisition failure. Without this the call renders as a
 * perfectly healthy "Connected / Listening..." session that cannot hear the
 * user — see the module header of `@/lib/micDiagnostics`.
 */
export const MicNotice: React.FC<MicNoticeProps> = ({ failure, compact }) => {
  const { t } = useTranslation()
  if (!failure) return null

  return (
    <div
      role="alert"
      className={`w-full rounded-md bg-amber-950/80 border border-amber-500/60 text-amber-100 ${
        compact ? 'text-xs px-2 py-1.5 max-w-xs' : 'text-base px-4 py-3 max-w-2xl'
      }`}
    >
      <p className="font-semibold">
        {t('digital_coworker:meetingBot.micUnavailable', {
          defaultValue: 'Microphone unavailable',
        })}
      </p>
      <p className={compact ? 'mt-0.5' : 'mt-1'}>{t(micFailureI18nKey(failure.reason))}</p>
    </div>
  )
}


interface ListenToggleProps {
  listening: boolean
  compact?: boolean
  onToggle: (listening: boolean) => void
}

/**
 * `/listenoff` and `/listenon` as a button (FS-711): an in-app call has no chat to type the
 * commands in. Shows what the bot hears now (the state the bot reports); a click asks for the
 * other, and the bot's confirmation flips the label.
 */
export const ListenToggle: React.FC<ListenToggleProps> = ({ listening, compact, onToggle }) => {
  const { t } = useTranslation()
  const label = listening
    ? t('digital_coworker:meetingBot.listenOn', { defaultValue: 'Listening on' })
    : t('digital_coworker:meetingBot.listenOff', { defaultValue: 'Listening off' })
  return (
    <button
      type="button"
      aria-pressed={listening}
      title={
        listening
          ? t('digital_coworker:meetingBot.listenOffHint', { defaultValue: 'Stop listening: talk among yourselves' })
          : t('digital_coworker:meetingBot.listenOnHint', { defaultValue: 'Listen again, even mid-lookup' })
      }
      onClick={() => onToggle(!listening)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 font-medium transition-colors ${
        compact ? 'py-1 text-xs' : 'py-1.5 text-sm'
      } ${
        listening
          ? 'border-blue-400/70 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30'
          : 'border-amber-400/70 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30'
      }`}
    >
      {listening ? <Ear className="w-3.5 h-3.5" aria-hidden /> : <EarOff className="w-3.5 h-3.5" aria-hidden />}
      {label}
    </button>
  )
}

export interface CallSurfaceProps {
  state: CallUiState
  compact?: boolean
  avatarSize: number
  videoSlot?: React.ReactNode
  /** Offered on a live call: `/listenon` / `/listenoff` as a button (nothing when absent). */
  onToggleListening?: (listening: boolean) => void
}

/**
 * The whole call UI in one component, driven by a plain `CallUiState`.
 */
export const CallSurface: React.FC<CallSurfaceProps> = ({
  state,
  compact,
  avatarSize,
  videoSlot,
  onToggleListening,
}) => (
  <>
    <AnimationStyles />
    <AgentAvatar
      agentPersona={state.persona}
      isTalking={state.states.talking}
      videoSlot={videoSlot}
      size={avatarSize}
    />
    <StatusDisplay
      connectionStatus={state.status}
      conversationStates={state.states}
      botEnabled={state.botEnabled}
      transcript={{ userText: state.userText, botText: state.botText }}
      agentPersona={state.persona}
      compact={compact}
    />
    {onToggleListening && state.status === 'connected' && state.botEnabled && (
      <div className={compact ? 'mt-1' : 'mt-2'}>
        <ListenToggle listening={state.states.botListening} compact={compact} onToggle={onToggleListening} />
      </div>
    )}
    <TranscriptDisplay
      transcript={{ userText: state.userText, botText: state.botText }}
      enabled={state.botEnabled && state.states.listening}
      compact={compact}
    />
    <MicNotice failure={state.micFailure} compact={compact} />
  </>
)
